import { sanitizePublicMessage } from "@/utils/errors/publicErrorContract";
import {
  CORRELATION_ID_HEADER,
  generateCorrelationId,
} from "@/utils/logging/correlationId";
import FrontendLoggerFactory from "@/utils/logging/FrontendLoggerFactory";
import { getBackendOrigin } from "@/utils/network/getBackendOrigin";

export interface DbSafetyInfo {
  isProduction: boolean;
  dbHost: string | null;
  dbName: string | null;
  checkedAtIso?: string;
}

interface DbSafetyResponse {
  isProduction?: unknown;
  dbHost?: unknown;
  dbName?: unknown;
  checkedAtIso?: unknown;
}

const toSafeErrorMessage = (error: unknown): string =>
  sanitizePublicMessage(error instanceof Error ? error.message : String(error));

const resolveCorrelationIdFromResponse = (
  response: Response,
  fallbackCorrelationId: string,
): string => {
  const responseCorrelationId =
    response.headers.get("x-correlation-id") ??
    response.headers.get("X-Correlation-ID");

  const normalizedCorrelationId = responseCorrelationId?.trim();
  if (!normalizedCorrelationId) {
    return fallbackCorrelationId;
  }

  return normalizedCorrelationId;
};

const buildDbSafetyUrl = (): string => {
  const endpoint = "/db-safety";
  const backendOrigin = getBackendOrigin();
  if (!backendOrigin) {
    return endpoint;
  }

  return `${backendOrigin}${endpoint}`;
};

/**
 * Reads backend `/db-safety` to determine whether admin is connected to production DB.
 */
export const fetchDbSafety = async (): Promise<DbSafetyInfo | null> => {
  const logger =
    FrontendLoggerFactory.getLogger().withComponent("DbSafetyService");
  const correlationId = generateCorrelationId();
  const requestLogger = logger.withCorrelationId(correlationId);
  const url = buildDbSafetyUrl();

  try {
    requestLogger.debug("Checking database safety status", {
      operation: "db_safety_start",
      url,
    });

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
        [CORRELATION_ID_HEADER]: correlationId,
      },
    });

    const responseCorrelationId = resolveCorrelationIdFromResponse(
      response,
      correlationId,
    );
    const responseLogger = requestLogger.withCorrelationId(
      responseCorrelationId,
    );

    if (!response.ok) {
      responseLogger.warn("DB safety check returned non-OK status", {
        operation: "db_safety_non_ok",
        url,
        status: response.status,
        statusText: response.statusText,
      });
      return null;
    }

    const raw = (await response.json()) as DbSafetyResponse;
    const isProduction =
      typeof raw.isProduction === "boolean" ? raw.isProduction : null;

    if (isProduction === null) {
      responseLogger.warn("DB safety check returned unexpected payload shape", {
        operation: "db_safety_invalid_payload",
        url,
      });
      return null;
    }

    responseLogger.info("DB safety check completed", {
      operation: "db_safety_success",
      url,
      isProduction,
    });

    return {
      isProduction,
      dbHost: typeof raw.dbHost === "string" ? raw.dbHost : null,
      dbName: typeof raw.dbName === "string" ? raw.dbName : null,
      checkedAtIso:
        typeof raw.checkedAtIso === "string" ? raw.checkedAtIso : undefined,
    };
  } catch (error) {
    requestLogger.warn("DB safety check failed with network/runtime error", {
      operation: "db_safety_error",
      url,
      error: toSafeErrorMessage(error),
    });
    return null;
  }
};
